import React, { useEffect, useState, useRef } from "react";
import { getDocs, collection, query, where, getDoc, doc } from "firebase/firestore";
import { db } from "../lib/firebase";
import { Kitchen, RateCardConfig, MenuItem } from "../types/index";
import { useLocationStore } from "../store/location.store";
import { Spinner } from "../components/common/Spinner";
import { LocationModal } from "../components/location/LocationModal";
import RotaryDial from "../components/home/RotaryDial";
import { MainDial } from "../components/home/MainDial";
import { DialOrderingFlow } from "../components/menu/DialOrderingFlow";
import CircleLogo from "../components/CircleLogo";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useCartStore } from "../store/cart.store";
import { CartItem } from "../types/index";
import { getRoadDistance } from "../utils/distance";
import { useAuth } from "../context/AuthProvider";
import { getOpenKitchens } from "../services/kitchen.service";

const Home: React.FC = () => {
  // Merged state from both branches
  const [kitchens, setKitchens] = useState<Kitchen[]>([]);
  const [nearbyKitchens, setNearbyKitchens] = useState<Kitchen[]>([]);
  const [myKitchens, setMyKitchens] = useState<Kitchen[]>([]);
  const [loading, setLoading] = useState(true);
  const [isCalculating, setIsCalculating] = useState(false);
  const [viewMode, setViewMode] = useState<'hero' | 'dial' | 'menu' | 'order' | 'subscribe'>('hero');
  const [maxRange, setMaxRange] = useState<number>(15);
  const [dialRotation, setDialRotation] = useState(60);
  const dialRotationRef = useRef(60);
  const isDraggingRef = useRef(false);
  const [debugMsg, setDebugMsg] = useState("");
  const [statusMessage, setStatusMessage] = useState("");
  const [showBanner, setShowBanner] = useState(true);
  const [customizingItem, setCustomizingItem] = useState<MenuItem | null>(null);
  const [isSelectionTriggered, setIsSelectionTriggered] = useState(false);
  const [assetsLoaded, setAssetsLoaded] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  const { coords, label, setPromptOpen } = useLocationStore();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { addItem: addToCart, items: cart, totalPrice: cartTotal } = useCartStore();

  const firstName = user?.name ? user.name.split(" ")[0].toUpperCase() : "NEIGHBOR";
  const REST_ANGLE = 60;
  const ORDER_ANGLE = 0;
  const SUBSCRIBE_ANGLE = 180;
  const TOLERANCE = 25;

  // Preload assets (from dial branch)
  useEffect(() => {
    const images = [
      "/images/pagebg.png", "/images/dial.png", "/images/fingerstop.png", 
      "/images/buttonbg.png", "/images/Ruchira-Flat-Logo.png", "/images/Ruchira-Banner.png",
      "/kitchenbg.png", "/placeholder-food.png"
    ];
    Promise.all(images.map(src => {
      const img = new Image();
      img.src = src;
      return img.decode().catch(() => {});
    })).then(() => setAssetsLoaded(true));
  }, []);

  // Location prompt (both branches)
  useEffect(() => {
    if (!coords && !label) {
      setPromptOpen(true);
    }
  }, [coords, label, setPromptOpen]);

  // Fetch config (from hero branch)
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const snap = await getDoc(doc(db, "config", "rates"));
        if (snap.exists()) {
          const data = snap.data() as RateCardConfig;
          if (data.maxDeliveryDistanceKm) setMaxRange(data.maxDeliveryDistanceKm);
        }
      } catch(e) { console.warn("Using default range", e); }
    };
    fetchConfig();
  }, []);

  // Fetch chef's kitchens if user is a chef
  useEffect(() => {
    const fetchMyKitchens = async () => {
      if (user?.roles?.includes("chef")) {
        try {
          const q = query(
            collection(db, "kitchens"), 
            where("ownerId", "==", user.uid)
          );
          const snapshot = await getDocs(q);
          const chefKitchens = snapshot.docs.map(doc => ({ 
            id: doc.id, 
            ...doc.data() 
          } as Kitchen));
          setMyKitchens(chefKitchens);
        } catch (error) {
          console.error("Error fetching chef kitchens:", error);
        }
      }
    };
    fetchMyKitchens();
  }, [user]);

  // Fetch kitchens (merged logic)
  useEffect(() => {
    const fetchKitchens = async () => {
      setLoading(true);
      setDebugMsg("Fetching kitchens...");
      
      try {
        // Get all open kitchens - FIXED: Remove destructuring
        const publicKitchens = await getOpenKitchens() || [];
        
        // Filter by distance if coords available
        if (coords && publicKitchens.length > 0) {
          setDebugMsg(`Calculating distances for ${publicKitchens.length} kitchens...`);
          setIsCalculating(true);
          
          const kitchensWithDistance = await Promise.all(
            publicKitchens.map(async (kitchen) => {
              const kLat = kitchen.lat || kitchen.latitude;
              const kLng = kitchen.lng || kitchen.longitude;
              
              if (!kLat || !kLng) return { ...kitchen, distance: 9999 };
              
              try {
                const dist = await getRoadDistance(coords, { lat: kLat, lng: kLng });
                return { ...kitchen, distance: dist };
              } catch {
                return { ...kitchen, distance: 9999 };
              }
            })
          );

          const sortedKitchens = kitchensWithDistance.sort((a, b) => (a.distance || 0) - (b.distance || 0));
          const inRangeKitchens = sortedKitchens.filter(k => (k.distance || 0) <= maxRange);
          
          setKitchens(inRangeKitchens.length > 0 ? inRangeKitchens : sortedKitchens);
          setNearbyKitchens(inRangeKitchens);
        } else {
          setKitchens(publicKitchens);
          setNearbyKitchens([]);
        }
      } catch (error) {
        console.error("Error fetching kitchens:", error);
        setDebugMsg("Error fetching data.");
      } finally {
        setLoading(false);
        setIsCalculating(false);
      }
    };

    if (assetsLoaded) fetchKitchens();
  }, [coords, maxRange, assetsLoaded]);

  // Dial handlers (from dial branch)
  const updateRotation = (val: number) => { 
    dialRotationRef.current = val; 
    setDialRotation(val); 
  };
  
  const handleManualRotate = (newRotation: number) => { 
    isDraggingRef.current = true; 
    updateRotation(newRotation); 
  };
  
  const handleDialRelease = () => {
    isDraggingRef.current = false;
    let currentRot = dialRotationRef.current % 360;
    if (currentRot > 180) currentRot -= 360;
    if (currentRot <= -180) currentRot += 360;

    if (viewMode === 'menu' && !customizingItem) {
      if (Math.abs(currentRot) <= TOLERANCE) { 
        if (!coords) { setPromptOpen(true); return; }
        updateRotation(ORDER_ANGLE); 
        setTimeout(() => setViewMode('order'), 300);
      } else if (Math.abs(currentRot) >= (180 - TOLERANCE)) { 
        updateRotation(SUBSCRIBE_ANGLE); 
        setTimeout(() => navigate('/subscription'), 300); 
      } else { 
        updateRotation(REST_ANGLE); 
      }
    } else if (viewMode === 'order' || customizingItem) {
      setIsSelectionTriggered(true); 
    }
  };

  const handleOrderComplete = (selection: any) => {
    const itemId = `${selection.dish.id}_${selection.variant || 'base'}`;
    const itemToAdd: CartItem = {
      id: itemId, 
      name: selection.dish.name, 
      price: selection.price / selection.quantity, 
      quantity: selection.quantity, 
      category: "Custom", 
      kitchenId: selection.kitchenId || "Kitchen", 
      kitchenName: "Kitchen", 
      photoUrl: selection.dish.photoUrl,
      customizations: { 
        variant: selection.variant, 
        category: selection.dish.category, 
        customizations: selection.customizations, 
        addons: selection.addons 
      }
    };
    addToCart(itemToAdd);
    setViewMode('menu');
    setCustomizingItem(null);
    setStatusMessage("ORDER ADDED TO CART ✓");
    setTimeout(() => setStatusMessage(""), 4000);
    updateRotation(REST_ANGLE);
  };

  if (!assetsLoaded) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-gray-900 flex items-center justify-center">
        <div className="text-center text-cyan-400 font-mono text-xl tracking-widest animate-pulse">
          INITIALIZING RUCHIRA...
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-gray-900 to-black flex flex-col font-mono overflow-hidden">
      <LocationModal />
      
      {/* Top Branding */}
      <motion.div 
        initial={{ y: -50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="absolute top-4 left-1/2 -translate-x-1/2 z-50 flex flex-col items-center pointer-events-none"
      >
        <img 
          src="/images/Ruchira-Flat-Logo.png" 
          alt="Ruchira" 
          className="h-14 w-auto drop-shadow-2xl"
        />
        <p className="text-[10px] uppercase tracking-[0.3em] text-cyan-300 font-bold mt-1">
          Home Food Network
        </p>
      </motion.div>

      {/* Hero / Dial Container */}
      <main className="flex-grow relative z-10 flex flex-col items-center justify-center p-8">
        
        {/* HERO VIEW */}
        {viewMode === 'hero' && (
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="w-full max-w-4xl mx-auto text-center space-y-12"
          >
            {/* Main Title */}
            <motion.div initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
              <h1 className="text-6xl md:text-7xl font-black text-transparent bg-clip-text bg-gradient-to-r from-orange-400 via-red-400 to-yellow-400 drop-shadow-2xl mb-6 font-serif tracking-tight">
                RUCHIRA
              </h1>
              <p className="text-xl md:text-2xl text-orange-100 font-light tracking-wide max-w-2xl mx-auto leading-relaxed">
                Bengaluru's Neighborhood Home Food Network
              </p>
            </motion.div>

            {/* Action Buttons */}
            <motion.div 
              initial={{ opacity: 0, y: 50 }} 
              animate={{ opacity: 1, y: 0 }} 
              className="grid md:grid-cols-2 gap-8 w-full max-w-2xl mx-auto"
            >
              <motion.button 
                whileHover={{ scale: 1.05, y: -5 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setViewMode('dial')}
                className="group relative bg-white/10 backdrop-blur-xl border-2 border-orange-500/40 p-8 rounded-3xl hover:bg-white/20 hover:border-orange-400 transition-all duration-500 shadow-2xl hover:shadow-orange-500/30 text-left overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-orange-500/10 to-yellow-500/10 opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="flex items-center gap-6">
                  <div className="w-20 h-20 bg-gradient-to-br from-orange-400 to-orange-600 rounded-2xl flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform">
                    <span className="text-3xl">🍽️</span>
                  </div>
                  <div>
                    <h3 className="text-2xl font-black text-white mb-2">Order Now</h3>
                    <p className="text-orange-100 text-lg font-light">Browse today's menu from nearby home chefs</p>
                  </div>
                </div>
              </motion.button>

              <motion.button 
                whileHover={{ scale: 1.05, y: -5 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => navigate('/subscription')}
                className="group relative bg-white/10 backdrop-blur-xl border-2 border-emerald-500/40 p-8 rounded-3xl hover:bg-white/20 hover:border-emerald-400 transition-all duration-500 shadow-2xl hover:shadow-emerald-500/30 text-left overflow-hidden"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/10 to-teal-500/10 opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="flex items-center gap-6">
                  <div className="w-20 h-20 bg-gradient-to-br from-emerald-400 to-teal-600 rounded-2xl flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform">
                    <span className="text-3xl">📅</span>
                  </div>
                  <div>
                    <h3 className="text-2xl font-black text-white mb-2">Meal Plans</h3>
                    <p className="text-emerald-100 text-lg font-light">Subscribe for daily/weekly tiffins from your favorite chefs</p>
                  </div>
                </div>
              </motion.button>
            </motion.div>
          </motion.div>
        )}

        {/* DIAL VIEW */}
        {viewMode === 'dial' && (
          <motion.div 
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            className="w-full max-w-2xl mx-auto flex flex-col items-center space-y-8 p-8"
          >
            {/* Back Button */}
            <motion.button 
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setViewMode('hero')}
              className="flex items-center gap-2 text-orange-300 hover:text-orange-100 text-lg font-bold uppercase tracking-wider bg-black/30 hover:bg-black/50 px-8 py-3 rounded-full backdrop-blur-md border border-orange-500/30 transition-all shadow-xl"
            >
              ← Back to Home
            </motion.button>

            {/* Kitchen Loading */}
            {loading ? (
              <div className="flex flex-col items-center space-y-6">
                <Spinner size={48} color="#f97316" />
                <div className="space-y-2 text-center">
                  <p className="text-xl font-semibold text-orange-100">{debugMsg}</p>
                  <p className="text-orange-300 text-sm">Scanning neighborhood kitchens...</p>
                </div>
              </div>
            ) : kitchens.length > 0 ? (
              <>
                <div className="text-center space-y-4">
                  <h2 className="text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-yellow-400 drop-shadow-2xl">
                    Nearby Kitchens ({kitchens.length})
                  </h2>
                  <p className="text-xl text-orange-100 max-w-md mx-auto">
                    Rotate dial to discover home chefs within {maxRange}km
                  </p>
                </div>
                <RotaryDial kitchens={kitchens.slice(0, 12)} />
              </>
            ) : (
              <div className="flex flex-col items-center space-y-6 text-center max-w-md mx-auto">
                <div className="w-24 h-24 bg-gradient-to-br from-gray-800 to-gray-900 rounded-3xl flex items-center justify-center shadow-2xl">
                  <span className="text-4xl">🏠</span>
                </div>
                <div className="space-y-3">
                  <h2 className="text-2xl font-bold text-orange-100">No Kitchens Nearby</h2>
                  <p className="text-orange-200 text-lg">No active home chefs found within {maxRange}km</p>
                  <div className="bg-black/40 p-6 rounded-2xl border border-orange-500/30 text-left space-y-2">
                    <p className="text-xs font-mono text-orange-300 uppercase tracking-wider">Debug</p>
                    <p className="text-sm text-orange-200">Location: {coords ? `${coords.lat.toFixed(4)}, ${coords.lng.toFixed(4)}` : "Not set"}</p>
                    <p className="text-sm text-orange-200">Range: {maxRange}km | Message: {debugMsg}</p>
                  </div>
                  <motion.button 
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setPromptOpen(true)}
                    className="px-12 py-4 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-bold text-lg rounded-3xl shadow-2xl hover:shadow-orange-500/50 transition-all"
                  >
                    🔄 Update Location
                  </motion.button>
                </div>
              </div>
            )}
          </motion.div>
        )}

        {/* DIAL ORDERING SYSTEM (from dial branch) */}
        {(viewMode === 'menu' || viewMode === 'order') && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="w-full h-screen overflow-hidden bg-slate-900 text-white font-mono"
          >
            {/* Top Branding */}
            <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-50 flex flex-col items-center">
              <img src="/images/Ruchira-Flat-Logo.png" alt="Ruchira" className="h-12 w-auto drop-shadow-2xl" />
              <p className="text-[10px] text-cyan-400 font-bold tracking-[0.3em] mt-1">NEIGHBORHOOD KITCHENS</p>
            </div>

            {/* Display Area */}
            <div className="absolute top-20 left-1/2 -translate-x-1/2 w-full max-w-sm h-32 flex items-center justify-center z-20 pointer-events-none">
              <div className="relative w-full h-full">
                <img src="/images/buttonbg.png" alt="Display" className="absolute inset-0 w-full h-full object-fill opacity-90 drop-shadow-2xl z-10" />
                <div className="absolute inset-0 z-20 flex items-center justify-center px-6">
                  <AnimatePresence mode="wait">
                    {isSearchOpen ? (
                      <motion.div 
                        key="search" 
                        initial={{ opacity: 0, scale: 0.95 }} 
                        animate={{ opacity: 1, scale: 1 }} 
                        exit={{ opacity: 0, scale: 0.95 }}
                        className="w-full flex items-center gap-3"
                      >
                        <input 
                          autoFocus 
                          type="text" 
                          value={searchQuery} 
                          onChange={(e) => setSearchQuery(e.target.value)}
                          placeholder="Search kitchens..." 
                          className="flex-1 bg-transparent border-none text-lg text-cyan-100 font-mono focus:outline-none placeholder-cyan-400" 
                        />
                        <button 
                          onClick={() => setIsSearchOpen(false)} 
                          className="text-red-400 hover:text-red-300 font-bold text-sm uppercase tracking-wider px-3 py-1 bg-black/40 rounded-full backdrop-blur transition-all"
                        >
                          Close
                        </button>
                      </motion.div>
                    ) : statusMessage ? (
                      <motion.div 
                        key="status" 
                        initial={{ opacity: 0, y: 10 }} 
                        animate={{ opacity: 1, y: 0 }} 
                        exit={{ opacity: 0, y: -10 }}
                        className="flex flex-col items-center w-full text-center"
                      >
                        <span className="text-cyan-300 font-bold text-xl tracking-[0.2em] uppercase animate-pulse drop-shadow-lg">
                          {statusMessage}
                        </span>
                        <div className="w-20 h-1 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-full mt-2 shadow-lg mx-auto"></div>
                      </motion.div>
                    ) : !coords ? (
                      <motion.button 
                        key="nolocation"
                        initial={{ opacity: 0 }} 
                        animate={{ opacity: 1 }} 
                        className="w-full text-center text-red-400 font-bold uppercase tracking-[0.3em] text-sm bg-red-900/30 border border-red-500/50 px-4 py-3 rounded-xl backdrop-blur hover:bg-red-900/50 transition-all cursor-pointer"
                        onClick={() => setPromptOpen(true)}
                      >
                        ⚠️ LOCATION NEEDED
                      </motion.button>
                    ) : isCalculating ? (
                      <motion.div 
                        key="calculating"
                        initial={{ opacity: 0 }} 
                        animate={{ opacity: 1 }} 
                        className="flex flex-col items-center space-y-1"
                      >
                        <Spinner size={24} color="#22d3ee" />
                        <span className="text-cyan-400 font-mono text-xs tracking-wider uppercase animate-pulse">SCANNING...</span>
                      </motion.div>
                    ) : (
                      <motion.div 
                        key="welcome" 
                        initial={{ opacity: 0 }} 
                        animate={{ opacity: 1 }} 
                        className="w-full flex flex-col items-center space-y-2"
                      >
                        <div className="flex items-center justify-between w-full px-2 text-xs">
                          <span className="font-bold uppercase tracking-wider text-cyan-300">HELLO, {firstName}</span>
                          <span className="font-bold text-green-400 border border-green-400 px-2 py-0.5 rounded text-xs tracking-wider">
                            {nearbyKitchens.length} KITCHENS
                          </span>
                        </div>
                        <button 
                          onClick={() => setIsSearchOpen(true)}
                          className="w-full bg-gradient-to-r from-cyan-900/50 to-blue-900/50 hover:from-cyan-800/70 hover:to-blue-800/70 border border-cyan-500/40 text-cyan-200 font-bold uppercase tracking-[0.2em] text-sm px-4 py-2.5 rounded-xl backdrop-blur-xl shadow-lg hover:shadow-cyan-500/20 transition-all"
                        >
                          🔍 QUICK SEARCH
                        </button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </div>

            {/* Chef Dashboard Button - FIXED: Added myKitchens.length check */}
            {user?.roles?.includes("chef") && myKitchens.length > 0 && viewMode === 'menu' && (
              <motion.button 
                initial={{ scale: 0 }} 
                animate={{ scale: 1 }} 
                className="absolute top-6 right-6 bg-gradient-to-r from-orange-500 to-orange-600 text-white px-6 py-2 rounded-2xl font-bold text-sm shadow-2xl hover:shadow-orange-500/50 transition-all z-50"
                onClick={() => navigate('/chef/dashboard')}
              >
                👨‍🍳 CHEF DASH
              </motion.button>
            )}

            {/* MAIN DIAL */}
            <div className="flex-1 flex items-center justify-center relative z-20 min-h-[60vh]">
              <div className="relative">
                <MainDial 
                  rotation={dialRotation} 
                  onRotate={handleManualRotate} 
                  onRelease={handleDialRelease} 
                />
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none z-30 w-20 h-20 flex items-center justify-center">
                  <img src="/images/fingerstop.png" alt="Dial Stop" className="w-full h-full object-contain drop-shadow-2xl" />
                </div>
              </div>

              {/* Dial Options Overlay */}
              {viewMode === 'menu' && !customizingItem && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-20">
                  <div className="absolute left-[20%] transform -translate-x-1/2 flex flex-col items-center gap-3 pointer-events-auto cursor-pointer" 
                       onClick={() => {
                         updateRotation(SUBSCRIBE_ANGLE);
                         setTimeout(() => navigate('/subscription'), 400);
                       }}>
                    <CircleLogo 
                      src="/host/Dosa.png" 
                      size={100} 
                      borderColor="#fbbf24" 
                      className="shadow-[0_0_30px_rgba(251,191,36,0.6)] hover:scale-110 transition-all"
                    />
                    <div className="bg-gradient-to-r from-amber-900/90 to-orange-900/90 text-amber-100 px-6 py-2.5 rounded-full border-2 border-amber-500/50 backdrop-blur-xl shadow-2xl font-bold text-sm tracking-wider hover:shadow-amber-500/40 hover:scale-105 transition-all">
                      SUBSCRIBE
                    </div>
                  </div>
                  
                  <div className="absolute right-[20%] transform translate-x-1/2 flex flex-col items-center gap-3 pointer-events-auto cursor-pointer" 
                       onClick={() => {
                         if (!coords) {
                           setPromptOpen(true);
                           return;
                         }
                         updateRotation(ORDER_ANGLE);
                         setTimeout(() => setViewMode('order'), 400);
                       }}>
                    <CircleLogo 
                      src="/host/idly.png" 
                      size={100} 
                      borderColor="#22d3ee" 
                      className="shadow-[0_0_30px_rgba(34,211,238,0.6)] hover:scale-110 transition-all"
                    />
                    <div className="bg-gradient-to-r from-cyan-900/90 to-blue-900/90 text-cyan-100 px-6 py-2.5 rounded-full border-2 border-cyan-500/50 backdrop-blur-xl shadow-2xl font-bold text-sm tracking-wider hover:shadow-cyan-500/40 hover:scale-105 transition-all">
                      ORDER NOW
                    </div>
                  </div>
                </div>
              )}

              {/* ORDERING FLOW */}
              {(viewMode === 'order' || customizingItem) && (
                <div className="absolute inset-0 z-30 flex items-center justify-center">
                  <DialOrderingFlow 
                    kitchens={nearbyKitchens}
                    item={customizingItem || undefined}
                    onComplete={handleOrderComplete} 
                    onCancel={() => { 
                      setViewMode('menu'); 
                      setCustomizingItem(null); 
                      setStatusMessage(""); 
                      updateRotation(REST_ANGLE); 
                    }}
                    dialRotation={dialRotation}
                    setDialRotation={setDialRotation}
                    onUpdateStatus={setStatusMessage}
                    isSelectionTriggered={isSelectionTriggered} 
                    onSelectionHandled={() => setIsSelectionTriggered(false)}
                  />
                </div>
              )}
            </div>

            {/* Cart Floating Button */}
            {cart.length > 0 && viewMode !== 'order' && !customizingItem && (
              <motion.div 
                initial={{ scale: 0, y: 100 }} 
                animate={{ scale: 1, y: 0 }}
                className="fixed bottom-8 left-1/2 -translate-x-1/2 z-[60] pointer-events-auto max-w-sm w-full px-4"
              >
                <motion.button
                  whileHover={{ scale: 1.05, y: -8 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => navigate("/cart")}
                  className="w-full bg-gradient-to-r from-cyan-500 via-blue-500 to-cyan-600 hover:from-cyan-600 hover:via-blue-600 hover:to-cyan-700 text-white px-8 py-5 rounded-3xl shadow-[0_0_40px_rgba(34,211,238,0.6)] hover:shadow-[0_0_60px_rgba(34,211,238,0.8)] border border-cyan-300/50 transition-all font-bold uppercase tracking-[0.3em] text-sm flex items-center justify-between"
                >
                  <span className="flex items-center gap-4">
                    <span className="text-2xl">🛒</span>
                    <div className="flex flex-col items-start">
                      <span className="text-xs font-semibold opacity-90">{cart.length} Items</span>
                      <span className="text-xl font-black">₹{cartTotal().toFixed(0)}</span>
                    </div>
                  </span>
                  <span className="text-2xl">→</span>
                </motion.button>
              </motion.div>
            )}
          </motion.div>
        )}
      </main>
    </div>
  );
};

export default Home;
